import { LitElement, html, TemplateResult, nothing } from 'lit';
import { property, customElement, query, queryAsync } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { FontSizes } from '../../../../types/common/types';
import { translateFn } from '../../../../utils/translator.helper';
import { RiskTypes, State } from '../../../common/constants';
import {
  handleWatchlistChangeButtonClick,
  overlayOpenListener,
  renderButtonGroup,
  renderRegions,
  renderSideMenuCompanyIfNeeded,
  renderSideMenuCompliance,
  renderSideMenuMitigationType,
  renderSideMenuOperatingSystem,
  renderSideMenuRiskScore,
  renderSideMenuSectionTitle,
  renderSideMenuState,
  renderSideMenuStatus,
  renderSideMenuWatchlist,
  RiskManagementSideMenuEvents,
  SideMenuButton,
} from '../../common/common';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { getWatchlistButtonTextBasedOnState } from '../../../common/utils';
import { ContextMenuOverlayElement } from '../../../../ui/gzContextMenuOverlay';
import { InitialDropdownDetails } from '../../components/userDetails/types/types';
import { UserExtraDetails } from '../../components/userDetails/types/types';
import { UserDetailsWebComponent } from '../../components/userDetails';
import { UserExtraDetailsWebComponent } from '../../components/userExtraDetails';

/**
 * @summary Type here the description of this component
 * @tag user-behavior-risks-side-menu
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('user-behavior-risks-side-menu')
export class UserBehaviorRisksSideMenuWebComponent extends LitElement {
  /**
   * See https://lit-element.polymer-project.org/guide/properties
   * Write here the description of this field
   */
  @property({ type: Object, attribute: false })
  item = {} as any;

  @property({ type: Object, attribute: false })
  userInitialDropdownDetails = null as null | InitialDropdownDetails;

  @property({ type: Object, attribute: false })
  initialUserDetails = null as null | any;

  @property({ type: Object, attribute: false })
  userDetails = null as null | any;

  @property({ type: Boolean, attribute: false })
  userDetailsHasErrors = false;

  @property({ type: Boolean, attribute: false })
  userDetailsEmptyState = false;

  @property({ type: Object, attribute: false })
  userExtraDetailsInitialDropdown = null as null | InitialDropdownDetails;

  @property({ type: Object, attribute: false })
  userExtraDetails = null as null | UserExtraDetails;

  @query('#watchlist-button')
  private readonly watchlistButton!: GzButtonWebComponent;

  @queryAsync('#extra-details-context-menu-side-panel')
  private readonly overlay!: ContextMenuOverlayElement;

  riskType = RiskTypes.USER_BEHAVIOUR_RISKS;

  @query('#user-details-component')
  public readonly userDetailsComponent!: UserDetailsWebComponent;

  @query('#user-extra-details-component')
  public readonly userExtraDetailsComponent!: UserExtraDetailsWebComponent;

  @property({ type: Boolean, attribute: true, reflect: true })
  showExtraDetailsPanel = false;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  render(): TemplateResult {
    if (this.showExtraDetailsPanel) {
      return html`${this.renderExtraDetailsPanel()}`;
    }

    return html`
      <div class="header mt-6">
        <gz-title fontSize="${FontSizes.HEADING_5}" class="mb-2 mt-3">
          ${Application.modules.riskManagement.controller.helpers.routinesTranslations.translateRoutineName(
            this.item.indicatorId,
            true
          )}
        </gz-title>
      </div>
      <div class="content">
        ${this.renderGeneralSection()} ${this.renderDetailsSection()} ${this.renderMitigationDetailsSection()}
        ${this.renderUserDetails()}
      </div>
      <gz-context-menu-overlay id="extra-details-context-menu-side-panel" @openContextMenu="${overlayOpenListener}">
      </gz-context-menu-overlay>
    `;
  }

  public changeWatchlistButtonState(isLoading: boolean) {
    this.watchlistButton.setLoadingState(isLoading);
  }

  renderGeneralSection(): TemplateResult {
    return html`
      <div class="section pb-2" data-section="general">
        ${renderSideMenuSectionTitle(translateFn('General'))}
        ${renderSideMenuRiskScore(this.item, this.overlay, this.riskType)} ${renderSideMenuStatus(this.item.status)}
        ${renderSideMenuState(this.item.state)} ${renderSideMenuOperatingSystem(this.item.operatingSystem)}
        ${renderSideMenuMitigationType(this.item.mitigation)} ${renderSideMenuCompanyIfNeeded(this.item.company?.name)}
        ${renderRegions(this.item.regions)} ${renderSideMenuCompliance(this.item.compliance)}
        ${renderSideMenuWatchlist(this.item.watchlist)}
        ${renderButtonGroup([
          {
            text: translateFn('View identities'),
            handler: this.handleViewUsersButtonClick,
            id: 'view-users-button',
          },
          {
            text: getWatchlistButtonTextBasedOnState(this.item.watchlist),
            handler: handleWatchlistChangeButtonClick.bind(this),
            id: 'watchlist-button',
          },
        ])}
      </div>
    `;
  }

  private renderDetailsSection(): TemplateResult {
    return html`
      <div class="section pb-2 pt-1" data-section="details">
        ${renderSideMenuSectionTitle(translateFn('Details'))}
        <gz-text class="text-description" fontSize="${FontSizes.PARAGRAPH_12}">
          ${unsafeHTML(
            Application.modules.riskManagement.controller.helpers.routinesTranslations.translateRoutineDescription(
              this.item.indicatorId,
              true
            )
          )}
        </gz-text>
        ${this.renderComplianceStandards()}
      </div>
    `;
  }

  private renderComplianceStandards() {
    if (this.item.compliance.length === 0) {
      return nothing;
    }

    const dialogContainer = document.querySelector('gz-user-behavior-risks-app')?.parentElement as HTMLElement;

    return html`
      <compliance-standards
        .indicatorId="${this.item.indicatorId}"
        .dialogContainer="${dialogContainer}"
      ></compliance-standards>
    `;
  }

  private renderMitigationDetailsSection(): TemplateResult {
    const buttons: Array<SideMenuButton> = [];
    if (this.item.state === State.IGNORED) {
      buttons.push({
        text: translateFn('Restore ignored risk'),
        handler: this.handleRestoreButtonClick,
        id: 'restore-risk-button',
        disabled: !this.item.company.specifics.features.iorUsable,
        tooltiptext: translateFn('Your current license does not provide access to this feature.'),
      });
    } else {
      buttons.push({
        text: translateFn('Ignore risk'),
        handler: this.handleIgnoreButtonClick,
        id: 'ignore-risk-button',
        disabled: !this.item.company.specifics.features.iorUsable,
        tooltiptext: translateFn('Your current license does not provide access to this feature.'),
      });
    }

    return html`
      <div class="section pb-2 pt-1" data-section="mitigation">
        ${renderSideMenuSectionTitle(translateFn('Risk mitigation'))}
        <gz-text class="pb-1 text-description" fontSize="${FontSizes.PARAGRAPH_12}">
          ${unsafeHTML(
            Application.modules.riskManagement.controller.helpers.routinesTranslations.translateRoutineAction(
              this.item.indicatorId,
              true
            )
          )}
        </gz-text>
        ${renderButtonGroup(buttons)}
      </div>
    `;
  }

  private renderUserDetails(): any {
    if (this.userDetailsEmptyState) {
      return nothing;
    }
    return html`
      <div class="section pb-2 pt-1" data-section="userDetails">
        <user-details
          id="user-details-component"
          .detailsTitle="${translateFn('Identities')}"
          .userInformation="${this.userDetails}"
          .initialDropdownDetails="${this.userInitialDropdownDetails}"
          ?showUserDropdown="${true}"
          ?hasErrors="${this.userDetailsHasErrors}"
          ?emptyState="${this.userDetailsEmptyState}"
        ></user-details>
      </div>
    `;
  }

  private handleViewUsersButtonClick(event: Event): void {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.VIEW_USERS_BY_HUMAN_RISKS, {
        detail: {
          companyId: this.item.company._id,
          indicatorsIds: [this.item.indicatorId],
        },
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  private async handleIgnoreButtonClick(event: Event): Promise<void> {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.IGNORE_HUMAN_RISKS, {
        detail: {
          indicatorsIds: [this.item.indicatorId],
          companyId: this.item.company._id,
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private async handleRestoreButtonClick(event: Event): Promise<void> {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.RESTORE_HUMAN_RISKS, {
        detail: {
          indicatorsIds: [this.item.indicatorId],
          companyId: this.item.company._id,
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private handleBackButtonClick() {
    this.showExtraDetailsPanel = false;
    this.userDetails = this.initialUserDetails;
  }

  private renderExtraDetailsPanel() {
    return html`
      <div class="header mt-2">
        <gz-button
          class="backBtn"
          type="quiet"
          color="secondary"
          buttonheight="small"
          buttonfontsize="small"
          @click="${this.handleBackButtonClick}"
        >
          <gz-icon class="backBtn-icon" color="secondary" size="24" name="ds-icon-arrow_left_collapse"></gz-icon>
          ${translateFn('Back')}
        </gz-button>
        <gz-title fontSize="${FontSizes.HEADING_5}" class="mb-2 mt-3"
          >${Application.modules.riskManagement.controller.helpers.routinesTranslations.translateRoutineName(
            this.item.indicatorId,
            true
          )}</gz-title
        >
      </div>
      <user-extra-details
        id="user-extra-details-component"
        .initialDropdownDetails="${this.userExtraDetailsInitialDropdown}"
        .itemsType="${this.userExtraDetails?.itemsType}"
        .items="${this.userExtraDetails?.items}"
        ?showUserDropdown="${true}"
      >
      </user-extra-details>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'user-behavior-risks-side-menu': UserBehaviorRisksSideMenuWebComponent;
  }
}
